import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AppTabPanel } from '../shared/tabs/AppTabPanel';
import { AppTabs } from '../shared/tabs/AppTabs';
import { FilterSelect } from '../shared/table/FilterSelect';
import {
    TICKET_IMPACT_OPTIONS,
    getTicketPriorityLabel,
    getTicketStatusLabel,
    getTicketTypeLabel,
    TICKET_PRIORITY_OPTIONS,
    TICKET_SOURCE_OPTIONS,
    TICKET_STATUS_OPTIONS,
    TICKET_TYPE_OPTIONS,
    TICKET_URGENCY_OPTIONS,
} from '../../../../helpers/ticket-label-helper';
import { TicketOpenTabsBar } from './shared';
import { TaskDetailLoadingState, TaskFollowSection, TaskDetailSidebar } from './components';
import {
    useFloatingBottomBar,
    useTicketDetailOpenTabs,
    useTaskDetailData,
    useTaskDetailActions,
    useTaskDetailScreenState,
} from './hooks';
import {
    ADDITIONAL_FIELD_TYPES,
    formatDateTime,
    getFollowAuthorAvatar,
    ticketTypeBadgeMap,
    ticketTypeIconMap,
} from './helpers';

export const TaskDetailScreen = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { ticketId } = useParams();
    const { token, ticketSignature } = useSelector((state) => state.auth);
    const { sidebarCollapsed, openTicketTabs, activeTicketTabId } = useSelector((state) => state.ui);

    const followEditorRef = React.useRef(null);
    const followComposerSectionRef = React.useRef(null);
    const followFilesInputRef = React.useRef(null);
    const mainColumnRef = React.useRef(null);
    const followSortMenuRef = React.useRef(null);

    const {
        ticket,
        attachments,
        resolutionAttachments,
        follows,
        followFilesById,
        categories,
        subcategories,
        users,
        groups,
        additionalTabs,
        additionalFieldDefs,
        additionalFieldValues,
        activeMainTab,
        isLoading,
        error,
        isEditingDescription,
        isContentTransitioning,
        tagInput,
        openSidebarPanels,
        form,
        mainTabs,
        handleInputChange,
        handleFormSelectChange,
        tagBadges,
        handleAddTag,
        handleRemoveTag,
        handleAdditionalFieldChange,
        handleAdditionalFieldSelectChange,
        handleToggleSidebarPanel,
        setForm,
        setTagInput,
        setIsEditingDescription,
        setActiveMainTab,
        loadTicket,
    } = useTaskDetailData({ token, ticketId });

    const {
        isSaving,
        isSavingFollow,
        isResolving,
        followIsPublic,
        followAttachments,
        composerMode,
        openAttachmentMenuId,
        followSortOrder,
        activeFollowTab,
        isFollowSortMenuOpen,
        setFollowIsPublic,
        setComposerMode,
        setActiveFollowTab,
        setIsFollowSortMenuOpen,
        handleSubmit,
        handleStartResolutionMode,
        handleDownloadAttachment,
        handleFollowEditorInput,
        handleFollowAttachmentsChange,
        handleOpenFollowFilesPicker,
        handleSelectFollowSortOrder,
        handleToggleAttachmentMenu,
        handleCreateFollow,
    } = useTaskDetailActions({
        token,
        ticketId,
        ticket,
        form,
        tagBadges,
        additionalFieldDefs,
        additionalFieldValues,
        additionalTabs,
        setActiveMainTab,
        loadTicket,
        followEditorRef,
        followFilesInputRef,
        followSortMenuRef,
        ticketSignature,
    });

    const { bottomBarStyle, isFloatingReady } = useFloatingBottomBar({ mainColumnRef, sidebarCollapsed });

    const {
        normalizedTicketTabs,
        handleSelectTicketTab,
        handleCloseTicketDetailTab,
        isTicketTabActive,
    } = useTicketDetailOpenTabs({
        dispatch,
        navigate,
        locationState: location.state,
        openTicketTabs,
        activeTicketTabId,
        ticketId,
    });

    const {
        sortedFollows,
        publicFollows,
        privateFollows,
        visibleFollows,
        followEmptyMessage,
        dueFromNow,
    } = useTaskDetailScreenState({
        dispatch,
        navigate,
        location,
        ticketId,
        ticket,
        follows,
        followSortOrder,
        activeFollowTab,
        normalizedTicketTabs,
    });

    if (isLoading) {
        return <TaskDetailLoadingState />;
    }

    if (!ticket) {
        return (
            <div className="alert alert-danger" role="alert">
                {error || 'No se encontró el ticket.'}
            </div>
        );
    }

    return (
        <>
            <div className={`row ticket-detail-layout px-2 px-md-3 ${isContentTransitioning ? 'ticket-detail-enter' : ''}`}>
                <div className="col-12 col-xxl-9 order-1 order-xxl-1 ticket-detail-main-col pt-2 ticket-detail-sidebar-scroll" ref={mainColumnRef}>
                <TicketOpenTabsBar
                    tabs={normalizedTicketTabs}
                    onSelectTab={handleSelectTicketTab}
                    onCloseTab={handleCloseTicketDetailTab}
                    isTabActive={isTicketTabActive}
                />

                <div className="card card__no-border ticket-detail-wrapper mb-3">
                    <div className="card-body px-3 py-2">
                        <div className="ticket-detail-header mb-3">
                            <div className="ticket-detail-header__content d-flex flex-column flex-md-row align-items-start align-items-md-center gap-2 gap-md-3">
                                <div  onClick={() => navigate('/tasks')} style={{cursor: 'pointer'}}>
                                    <i className="bi bi-arrow-left"></i>&nbsp;
                                </div>
                                <div>
                                    <h5 className="fw-bold mb-1 ticket-detail-header__title">{ticket.code} · {ticket.title}</h5>
                                    <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                                        <span className={`badge d-inline-flex align-items-center gap-1 ${ticketTypeBadgeMap[String(form.type || '').toUpperCase()] || 'bg-soft-secondary text-secondary'}`}>
                                            <i className={ticketTypeIconMap[String(form.type || '').toUpperCase()] || 'bi bi-tag'} />
                                            <span>{getTicketTypeLabel(form.type)}</span>
                                        </span>
                                        <div>
                                            <div className="text-muted fs-12">Creado en: {formatDateTime(ticket.createdDate)}</div>
                                            <div className="text-muted fs-12">Última actividad: {formatDateTime(ticket.lastActivityDate || ticket.updatedDate)}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className="ticket-detail-main-tabs mb-3">
                                <AppTabs
                                    tabs={mainTabs}
                                    activeTab={activeMainTab}
                                    onChange={setActiveMainTab}
                                    navClassName="nav flex-row flex-wrap ticket-detail-top-tabs"
                                    itemClassName="nav-item"
                                />
                            </div>

                            <div className="tab-content border rounded p-3">
                                <AppTabPanel isActive={activeMainTab === 'content'} className="p-0">
                                    <div className="row g-2">
                                        <div className="col-12">
                                            <label className="form-label">Título</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="title"
                                                value={form.title}
                                                onChange={handleInputChange}
                                                required
                                            />
                                        </div>

                                        <div className="col-12">
                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                <label className="form-label mb-0">Descripción</label>
                                                {!isEditingDescription ? (
                                                    <button
                                                        type="button"
                                                        className="btn btn-light-brand btn-sm"
                                                        onClick={() => setIsEditingDescription(true)}
                                                    >
                                                        Editar descripción
                                                    </button>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        className="btn btn-light btn-sm"
                                                        onClick={() => {
                                                            setForm((previous) => ({
                                                                ...previous,
                                                                description: ticket?.description || '',
                                                            }));
                                                            setIsEditingDescription(false);
                                                        }}
                                                    >
                                                        Cancelar edición
                                                    </button>
                                                )}
                                            </div>

                                            {!isEditingDescription ? (
                                                <div
                                                    className="ticket-html-content"
                                                    style={{ minHeight: '120px' }}
                                                    dangerouslySetInnerHTML={{
                                                        __html: form.description || '<p class="text-muted mb-0">Sin descripción</p>',
                                                    }}
                                                />
                                            ) : (
                                                <textarea
                                                    className="form-control"
                                                    rows={8}
                                                    name="description"
                                                    value={form.description}
                                                    onChange={handleInputChange}
                                                    required
                                                />
                                            )}
                                        </div>

                                    </div>
                                </AppTabPanel>

                                <AppTabPanel isActive={activeMainTab === 'content'} className="p-0">
                                    <TaskFollowSection
                                        followComposerSectionRef={followComposerSectionRef}
                                        composerMode={composerMode}
                                        setComposerMode={setComposerMode}
                                        followIsPublic={followIsPublic}
                                        setFollowIsPublic={setFollowIsPublic}
                                        followEditorRef={followEditorRef}
                                        handleFollowEditorInput={handleFollowEditorInput}
                                        followFilesInputRef={followFilesInputRef}
                                        handleFollowAttachmentsChange={handleFollowAttachmentsChange}
                                        handleOpenFollowFilesPicker={handleOpenFollowFilesPicker}
                                        handleCreateFollow={handleCreateFollow}
                                        isSavingFollow={isSavingFollow}
                                        isResolving={isResolving}
                                        followAttachments={followAttachments}
                                        activeFollowTab={activeFollowTab}
                                        setActiveFollowTab={setActiveFollowTab}
                                        sortedFollows={sortedFollows}
                                        publicFollows={publicFollows}
                                        privateFollows={privateFollows}
                                        followSortMenuRef={followSortMenuRef}
                                        isFollowSortMenuOpen={isFollowSortMenuOpen}
                                        setIsFollowSortMenuOpen={setIsFollowSortMenuOpen}
                                        followSortOrder={followSortOrder}
                                        handleSelectFollowSortOrder={handleSelectFollowSortOrder}
                                        resolutionAttachments={resolutionAttachments}
                                        openAttachmentMenuId={openAttachmentMenuId}
                                        handleToggleAttachmentMenu={handleToggleAttachmentMenu}
                                        handleDownloadAttachment={handleDownloadAttachment}
                                        visibleFollows={visibleFollows}
                                        followEmptyMessage={followEmptyMessage}
                                        followFilesById={followFilesById}
                                        formatDateTime={formatDateTime}
                                        getFollowAuthorAvatar={getFollowAuthorAvatar}
                                    />
                                </AppTabPanel>

                                <AppTabPanel isActive={activeMainTab === 'classification'} className="p-0">
                                    <div className="row g-2">
                                        <div className="col-md-6">
                                            <label className="form-label">Tipo</label>
                                            <FilterSelect
                                                value={form.type}
                                                options={TICKET_TYPE_OPTIONS}
                                                onChange={(nextValue) => handleFormSelectChange('type', nextValue)}
                                            />
                                        </div>

                                        <div className="col-md-6">
                                            <label className="form-label">Fuente</label>
                                            <FilterSelect
                                                value={form.source}
                                                options={TICKET_SOURCE_OPTIONS}
                                                onChange={(nextValue) => handleFormSelectChange('source', nextValue)}
                                            />
                                        </div>

                                        <div className="col-md-6">
                                            <label className="form-label">Prioridad</label>
                                            <FilterSelect
                                                value={form.priority}
                                                options={TICKET_PRIORITY_OPTIONS}
                                                onChange={(nextValue) => handleFormSelectChange('priority', nextValue)}
                                            />
                                        </div>

                                        <div className="col-md-6">
                                            <label className="form-label">Impacto</label>
                                            <FilterSelect
                                                value={form.impact}
                                                options={TICKET_IMPACT_OPTIONS}
                                                onChange={(nextValue) => handleFormSelectChange('impact', nextValue)}
                                            />
                                        </div>

                                        <div className="col-md-6">
                                            <label className="form-label">Urgencia</label>
                                            <FilterSelect
                                                value={form.urgency}
                                                options={TICKET_URGENCY_OPTIONS}
                                                onChange={(nextValue) => handleFormSelectChange('urgency', nextValue)}
                                            />
                                        </div>
                                        <div className="col-md-6"> </div>

                                        <div className="col-md-6">
                                            <label className="form-label">Categoría</label>
                                            <FilterSelect
                                                value={form.category}
                                                options={[
                                                    { value: '', label: 'Sin categoría' },
                                                    ...categories.map((category) => ({ value: category._id, label: category.name })),
                                                ]}
                                                onChange={(nextValue) => handleFormSelectChange('category', nextValue)}
                                            />
                                        </div>

                                        <div className="col-md-6">
                                            <label className="form-label">Subcategoría</label>
                                            <FilterSelect
                                                value={form.subcategory}
                                                options={[
                                                    { value: '', label: 'Sin subcategoría' },
                                                    ...subcategories.map((subcategory) => ({ value: subcategory._id, label: subcategory.name })),
                                                ]}
                                                onChange={(nextValue) => handleFormSelectChange('subcategory', nextValue)}
                                            />
                                        </div>
                                    </div>
                                </AppTabPanel>

                                {additionalTabs.map((tab) => (
                                    <AppTabPanel key={`panel-${tab.key}`} isActive={activeMainTab === `additional-${tab.key}`} className="p-0">
                                        <div className="mb-3">
                                            <div className="row g-2">
                                                {tab.fields.map((field) => {
                                                    const fieldKey = String(field.key || '');
                                                    if (!fieldKey) {
                                                        return null;
                                                    }

                                                    const fieldLabel = `${field.label}${field.required ? ' *' : ''}`;
                                                    const fieldType = field.type;

                                                    if (fieldType === ADDITIONAL_FIELD_TYPES.CHECKBOX) {
                                                        return (
                                                            <div className="col-12" key={`${tab.key}-${fieldKey}`}>
                                                                <div className="form-check mt-2">
                                                                    <input
                                                                        type="checkbox"
                                                                        className="form-check-input"
                                                                        id={`detail-custom-field-${fieldKey}`}
                                                                        checked={Boolean(additionalFieldValues[fieldKey])}
                                                                        onChange={(event) => handleAdditionalFieldChange(event, fieldKey)}
                                                                    />
                                                                    <label className="form-check-label" htmlFor={`detail-custom-field-${fieldKey}`}>
                                                                        &nbsp;{fieldLabel}
                                                                    </label>
                                                                </div>
                                                            </div>
                                                        );
                                                    }

                                                    if (fieldType === ADDITIONAL_FIELD_TYPES.LONG_TEXT) {
                                                        return (
                                                            <div className="col-12" key={`${tab.key}-${fieldKey}`}>
                                                                <label className="form-label">{fieldLabel}</label>
                                                                <textarea
                                                                    className="form-control"
                                                                    rows={4}
                                                                    value={additionalFieldValues[fieldKey] ?? ''}
                                                                    onChange={(event) => handleAdditionalFieldChange(event, fieldKey)}
                                                                    required={Boolean(field.required)}
                                                                />
                                                            </div>
                                                        );
                                                    }

                                                    if (fieldType === ADDITIONAL_FIELD_TYPES.LIST) {
                                                        const options = Array.isArray(field.options) ? field.options : [];
                                                        return (
                                                            <div className="col-md-6" key={`${tab.key}-${fieldKey}`}>
                                                                <label className="form-label">{fieldLabel}</label>
                                                                <FilterSelect
                                                                    value={additionalFieldValues[fieldKey] ?? ''}
                                                                    options={[
                                                                        { value: '', label: 'Selecciona una opción' },
                                                                        ...options.map((option) => ({
                                                                            value: option,
                                                                            label: option,
                                                                        })),
                                                                    ]}
                                                                    onChange={(nextValue) => handleAdditionalFieldSelectChange(fieldKey, nextValue)}
                                                                />
                                                            </div>
                                                        );
                                                    }

                                                    return (
                                                        <div className="col-md-6" key={`${tab.key}-${fieldKey}`}>
                                                            <label className="form-label">{fieldLabel}</label>
                                                            <input
                                                                type={fieldType === ADDITIONAL_FIELD_TYPES.NUMBER ? 'number' : 'text'}
                                                                className="form-control form-control-sm"
                                                                value={additionalFieldValues[fieldKey] ?? ''}
                                                                onChange={(event) => handleAdditionalFieldChange(event, fieldKey)}
                                                                required={Boolean(field.required)}
                                                            />
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </AppTabPanel>
                                ))}
                            </div>

                        </form>
                    </div>
                </div>

                <div
                    className={`ticket-detail-bottom-actions ${isFloatingReady ? 'ticket-detail-bottom-actions--floating' : ''} px-4 mt-2`}
                    style={isFloatingReady ? bottomBarStyle : undefined}
                >
                    <div className="ticket-detail-bottom-actions__inner">
                        <div className="ticket-detail-bottom-actions__status d-flex align-items-center gap-2">
                            <label className="form-label m-0">Estatus</label>
                            <FilterSelect
                                value={form.status}
                                options={TICKET_STATUS_OPTIONS}
                                menuPlacement="top"
                                onChange={(nextValue) => handleFormSelectChange('status', nextValue)}
                            />
                        </div>

                        <div className="ticket-detail-bottom-actions__buttons d-flex flex-wrap gap-2">
                            <button type="button" className="btn btn-primary" disabled={isSaving} onClick={handleSubmit}>
                                <i className="bi bi-cloud"></i>&nbsp;
                                {isSaving ? 'Guardando...' : 'Guardar'}
                            </button>
                            <button type="button" className="btn btn-success" disabled={isResolving} onClick={handleStartResolutionMode}>
                                <i className="bi bi-check2-circle"></i>&nbsp;
                                {composerMode === 'resolution' ? 'Modo resolución activo' : 'Resolución'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <TaskDetailSidebar
                openSidebarPanels={openSidebarPanels}
                handleToggleSidebarPanel={handleToggleSidebarPanel}
                form={form}
                getTicketStatusLabel={getTicketStatusLabel}
                ticketTypeBadgeMap={ticketTypeBadgeMap}
                ticketTypeIconMap={ticketTypeIconMap}
                getTicketTypeLabel={getTicketTypeLabel}
                getTicketPriorityLabel={getTicketPriorityLabel}
                dueFromNow={dueFromNow}
                formatDateTime={formatDateTime}
                ticket={ticket}
                users={users}
                groups={groups}
                handleFormSelectChange={handleFormSelectChange}
                getFollowAuthorAvatar={getFollowAuthorAvatar}
                attachments={attachments}
                openAttachmentMenuId={openAttachmentMenuId}
                handleToggleAttachmentMenu={handleToggleAttachmentMenu}
                handleDownloadAttachment={handleDownloadAttachment}
                tagBadges={tagBadges}
                handleRemoveTag={handleRemoveTag}
                tagInput={tagInput}
                setTagInput={setTagInput}
                handleAddTag={handleAddTag}
            />
            </div>

            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar
                newestOnTop
                closeOnClick
                pauseOnHover
                draggable
                theme="light"
            />
        </>
    );
};
